import { type PaymentProviderConfiguration } from './ProviderConfiguration';

/**
 * Whether a vendor configuration blob holds anything the operator actually filled in (TTD-168).
 *
 * **Only strings count.** Every required field in these vendor configs is a string; the numeric and boolean
 * members are optional settings that arrive as their type defaults — `0`, `false` — on any round trip through
 * the form, whether or not anyone touched them.
 *
 * That distinction is the whole bug. The vendor APIs already exempt an untouched configuration from
 * required-field validation, but their rule treats a number or a boolean as a deliberate value ("false is not
 * blank"). FreedomPay's AFCC config carries four ints and a bool alongside its six required strings, so a
 * completely blank AFCC template still read as *configured*, its required fields were enforced, and choosing
 * FreedomPay was refused outright — with a list of fields for an integration nobody had asked for, whether
 * AFCC was enabled or not.
 *
 * Fixing it here rather than in each vendor API means one change instead of nine, and it treats the cause:
 * the portal should not be posting configurations nobody edited.
 */
export const isUnconfigured = (configurationJson?: string | null): boolean => {
    if (!configurationJson || !configurationJson.trim()) return true;

    let parsed: unknown;
    try {
        parsed = JSON.parse(configurationJson);
    } catch {
        // Malformed JSON is not "untouched" — send it and let the vendor report it properly.
        return false;
    }

    return !hasTypedValue(parsed);
};

const hasTypedValue = (value: unknown): boolean => {
    if (typeof value === 'string') return value.trim() !== '';
    if (Array.isArray(value)) return value.some(hasTypedValue);
    if (value && typeof value === 'object') return Object.values(value).some(hasTypedValue);
    // Numbers, booleans, null, undefined: not evidence that anything was configured.
    return false;
};

/**
 * The payment configuration with untouched integrations dropped.
 *
 * An integration nobody filled in has no row to write, so not sending it is the same outcome without the
 * refusal. One the operator HAS filled in — even partly — still goes, so a half-configured integration is
 * still reported by the vendor rather than silently accepted.
 */
export const withConfiguredIntegrationsOnly = (
    payment: PaymentProviderConfiguration
): PaymentProviderConfiguration => {
    if (!payment.integrationConfigurations?.length) return payment;

    return {
        ...payment,
        integrationConfigurations: payment.integrationConfigurations.filter(
            (integration) => !isUnconfigured(integration.configurationJson)
        ),
    };
};
